import { SelectionModel } from '@angular/cdk/collections';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { each, get, hasIn } from 'lodash-es';
import {
  ToasterService,
  ToastStatus,
} from 'src/app/core/toaster/toaster.service';
import {
  Action,
  BoardStatus,
  Sign,
  SignService,
} from 'src/app/signs/sign.service';
import { SignTableConfirmDialogComponent } from '../sign-table-confirm-dialog/sign-table-confirm-dialog.component';

interface SelectValue {
  value: string;
  viewValue: string;
}

@Component({
  selector: 'app-sign-table-admin',
  templateUrl: './sign-table-admin.component.html',
  styleUrls: ['./sign-table-admin.component.scss'],
})
export class SignTableAdminComponent implements OnInit, AfterViewInit {
  form: FormGroup = new FormGroup({
    receveidAtStart: new FormControl<Date | null>(null),
    receveidAtEnd: new FormControl<Date | null>(null),
    completedAtStart: new FormControl<Date | null>(null),
    completedAtEnd: new FormControl<Date | null>(null),
  });

  // date and time format can have "style" options (i.e. full, long, medium, short)
  enGBFormatter = new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'short',
  });

  displayedColumns: string[] = [
    'board.houseNumber',
    'board.address',
    'board.city',
    'board.postalCode',
    'numBoards',
    'actionRequested',
    'toRead.description',
    'status',
    'branch.name',
    'ba.scheduleTo',
    'receivedAt',
    'scheduledAt',
    'completedAt',
    'note',
    'adminNotes',
    'delete',
  ];

  displayedColumnsFooter: string[] = ['board.houseNumber'];

  dataSource: Sign[] = [];

  userSelectValues: SelectValue[] = [
    {
      value: 'c88ac485-f823-4418-b1dd-e0e66a9af631',
      viewValue: 'Company Test',
    },
  ];

  // Pagination
  length = 0;
  pageSize = 10;
  pageIndex = 0;
  pageSizeOptions = [5, 10, 25, 50, 100];
  hidePageSize = false;
  showPageSizeOptions = true;
  showFirstLastButtons = true;
  disabled = false;
  pageEvent: PageEvent = new PageEvent();

  // Sorting
  @ViewChild(MatSort) matSort!: MatSort;
  sort: { active: string; direction: 'asc' | 'desc' | '' } = {
    active: 'createdAt',
    direction: '',
  };

  // Filters
  filterStatus: BoardStatus | string = 'null';
  filterScheduledTo: string = '';
  filterActionType: Action | string = '';
  searchQuery: string = '';

  // Loading state
  isLoading: boolean = false;

  // Reference data
  admins: any[] = [];
  actions: any[] = [
    { id: 'E', description: 'Erect' },
    { id: 'R', description: 'Re-erect' },
    { id: 'C', description: 'Change Slip' },
    { id: 'COL', description: 'Collect' },
    { id: 'STR', description: 'Stray Board' },
  ];

  constructor(
    private signService: SignService,
    private router: Router,
    private toasterService: ToasterService,
    public dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {}

  fetchItems(limit: number, page: number): void {
    this.isLoading = true;

    // Build params object, only including non-null/non-empty values
    const params: any = {
      limit,
      page,
      isActive: true,
    };

    // Only add status if it's a real value (not null or 'null')
    if (this.filterStatus && this.filterStatus !== 'null') {
      params.status = this.filterStatus;
    }

    // Only add scheduleTo if it has a value
    if (this.filterScheduledTo && this.filterScheduledTo !== '') {
      params.scheduleTo = this.filterScheduledTo;
    }

    // Only add actionRequested if it has a value
    if (this.filterActionType && this.filterActionType !== '') {
      params.actionRequested = this.filterActionType;
    }

    // Only add orderBy if there's a sort direction
    if (this.sort.direction !== '') {
      params.orderBy = [
        `${this.sort.active} ${this.sort.direction.toUpperCase()}`,
      ];
    }

    // Only add search if there's a query
    if (this.searchQuery && this.searchQuery.trim() !== '') {
      params.search = this.searchQuery.trim();
    }

    this.signService.fetchBoardActions(params).subscribe({
      next: value => {
        const meta = value.meta;
        this.length = meta.totalItems;

        const items = value.items;
        this.dataSource = items;
        this.isLoading = false;
      },
      error: error => {
        console.error('Error fetching items:', error);
        this.isLoading = false;
        this.toasterService.toast(
          ToastStatus.error,
          'Failed to load data. Please try again.'
        );
      },
    });

    this.signService.fetchAdmins().subscribe(values => {
      this.admins = get(values, 'items', []);
    });
  }

  ngOnInit(): void {
    this.fetchItems(this.pageSize, this.pageIndex);
  }

  ngAfterViewInit(): void {
    // Initialize MatSort with default sort state after view is ready
    setTimeout(() => this.syncMatSortState(), 0);
  }

  private syncMatSortState(): void {
    if (this.matSort) {
      // Only update if there's an active sort
      if (this.sort.direction) {
        this.matSort.active = this.sort.active;
        this.matSort.direction = this.sort.direction as 'asc' | 'desc' | '';
      }
      this.cdr.detectChanges();
    }
  }

  // Helper method for status color (used in template)
  statusColor(status: BoardStatus): string {
    switch (status) {
      case BoardStatus.scheduled:
        return 'accent';
      case BoardStatus.pending:
        return 'warn';
      case BoardStatus.completed:
        return 'primary';
      case BoardStatus.rejected:
        return 'warn';
    }
  }

  // Filter methods
  hasActiveFilters(): boolean {
    return (
      (this.filterStatus !== null && this.filterStatus !== 'null') ||
      this.filterScheduledTo !== '' ||
      this.filterActionType !== '' ||
      this.searchQuery !== '' ||
      this.form.get('receveidAtStart')?.value !== null ||
      this.form.get('completedAtStart')?.value !== null
    );
  }

  clearFilters(): void {
    this.filterStatus = 'null';
    this.filterScheduledTo = '';
    this.filterActionType = '';
    this.searchQuery = '';
    this.form.reset();
    this.pageIndex = 0;
    this.fetchItems(this.pageSize, this.pageIndex);
  }

  removeFilter(filterType: string): void {
    switch (filterType) {
      case 'status':
        this.filterStatus = 'null';
        break;
      case 'assignee':
        this.filterScheduledTo = '';
        break;
      case 'action':
        this.filterActionType = '';
        break;
    }
    this.pageIndex = 0;
    this.fetchItems(this.pageSize, this.pageIndex);
  }

  getAssigneeName(userId: string): string {
    const admin = this.admins.find(a => a.id === userId);
    return admin ? admin.name : userId;
  }

  getActionName(actionId: string): string {
    const action = this.actions.find(a => a.id === actionId);
    return action ? action.description : actionId;
  }

  // Search
  handleSearch(): void {
    this.pageIndex = 0;
    this.fetchItems(this.pageSize, this.pageIndex);
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.handleSearch();
  }

  // Event handlers
  announceSortChange(sortState: Sort) {
    this.sort = {
      active: sortState.active,
      direction: sortState.direction as 'asc' | 'desc' | '',
    };
    this.pageIndex = 0; // Reset to first page when sorting changes
    this.fetchItems(this.pageSize, this.pageIndex);
  }

  handleStatusChanged(value: string) {
    this.pageIndex = 0;
    this.fetchItems(this.pageSize, this.pageIndex);
  }

  handleDateChanged(event: any, dateType: string) {
    this.pageIndex = 0;
    this.fetchItems(this.pageSize, this.pageIndex);
  }

  handlePageEvent(e: PageEvent) {
    this.pageEvent = e;
    this.length = e.length;
    this.pageSize = e.pageSize;
    this.pageIndex = e.pageIndex;
    this.fetchItems(this.pageSize, this.pageIndex);
  }

  setPageSizeOptions(setPageSizeOptionsInput: string) {
    if (setPageSizeOptionsInput) {
      this.pageSizeOptions = setPageSizeOptionsInput
        .split(',')
        .map(str => +str);
    }
  }

  // Utility
  min(a: number, b: number): number {
    return Math.min(a, b);
  }

  // Actions
  handleEdit(sign: any) {
    if (sign.status == BoardStatus.scheduled) return;

    if (!sign.scheduleAt || sign.scheduleAt == '') {
      this.toasterService.toast(
        ToastStatus.error,
        'Missing date for Schedule work!'
      );
      return;
    }

    if (!sign.scheduleTo || sign.scheduleTo == '') {
      this.toasterService.toast(
        ToastStatus.error,
        'Missing user to Schedule work!'
      );
      return;
    }

    if (sign.status == 'pending') {
      let updateSign = {
        id: sign.id,
        status: BoardStatus.scheduled,
        scheduleAt: sign.scheduleAt,
        scheduleTo: sign.scheduleTo,
      };

      let handleError = (error: Error) => {
        this.toasterService.toast(ToastStatus.error, 'Try again later.');
        console.error('Error!: ', error);
      };

      sign.status = BoardStatus.scheduled;
      this.signService.scheduleSign(updateSign).subscribe({
        next: sign => {
          this.toasterService.toast(ToastStatus.success, 'Edit successful');
        },
        error: error => handleError(error),
      });
    }
  }

  toogleShowNotes(sign: Sign) {
    sign.hasNotes = !sign.hasNotes;
  }

  scheduleToValueChange(value: string, element: any) {
    element.scheduleTo = value;
  }

  scheduleAtValueChange(event: any, element: any) {
    element.scheduleAt = event.value.toISOString();
  }

  openDialog(
    signId: string,
    enterAnimationDuration: string,
    exitAnimationDuration: string
  ): void {
    const dialogRef = this.dialog.open(SignTableConfirmDialogComponent, {
      width: '350px',
      enterAnimationDuration,
      exitAnimationDuration,
      data: { signId },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.fetchItems(this.pageSize, this.pageIndex);
      }
    });
  }

  handleDelete(signId: string) {
    this.openDialog(signId, '0ms', '0ms');
  }

  handleNew() {
    this.router.navigate(['admin', 'sign']);
  }
}
